/** The scene strip's tiles (docs/SPEC.md §6.1): a frame of each built scene,
 *  drawn by the player in the browser (`film/frames.ts::frameOf`) at the
 *  scene's opening moment, as a picture's address by scene id. A scene is
 *  framed once per bake (its manifest's link without the query, which a new
 *  reading signs again), one at a time; one that cannot be drawn keeps its
 *  grey tile. The film's environment of the page (`FilmEnvContext`, the
 *  overlay's) is handed to the offscreen player, as tests give a page's. */
import {
  createElement,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { FilmEnvContext } from "../film/context";
import {
  type FrameSize,
  frameOf,
  frameUrl,
  openingMoment,
} from "../film/frames";
import type { Manifest } from "../film/manifest";
import { OverlayFrameContext } from "../film/useOverlayFrame";

/** What a frame is drawn from: the scene and its bake. */
const bakeOf = (scene: Manifest["scenes"][number]): string =>
  `${scene.id}|${(scene.glb_url ?? "").split("?")[0]}`;

export function useSceneFrames(
  manifest: Manifest | null,
  size: FrameSize,
): Record<string, string> {
  const env = useContext(FilmEnvContext);
  const overlay = useContext(OverlayFrameContext);
  const [frames, setFrames] = useState<Record<string, string>>({});
  /** The bakes framed or being framed (each once). */
  const asked = useRef(new Set<string>());

  useEffect(() => {
    if (!manifest) return;
    const wrap = (node: ReactNode): ReactNode => {
      const inner = overlay
        ? createElement(OverlayFrameContext.Provider, { value: overlay }, node)
        : node;
      return env
        ? createElement(FilmEnvContext.Provider, { value: env }, inner)
        : inner;
    };
    for (const scene of manifest.scenes) {
      const bake = bakeOf(scene);
      if (!scene.ready || asked.current.has(bake)) continue;
      asked.current.add(bake);
      // frameOf draws one frame at a time, in the order asked
      frameOf(manifest, scene.id, openingMoment(scene), size, { wrap }).then(
        (bitmap) => {
          const url = frameUrl(bitmap);
          bitmap.close?.();
          setFrames((f) => ({ ...f, [scene.id]: url }));
        },
        () => undefined, // the tile stays grey; the scene still plays
      );
    }
  }, [manifest, size, env, overlay]);

  return frames;
}
